import { useEffect, useMemo, useRef, useState } from "react";
import { api, ANLAGEN_TYPEN, ANLAGEN_ART, ANREDE, STATUS_LABELS, UNTERLAGEN_LABELS, UNTERLAGEN_CLS, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AnmeldungForm } from "@/components/AnmeldungForm";
import { Plus, Search, Pencil, Trash2, CalendarPlus, Building2, Users, Zap, FileDown, FileText as FileTextIcon, X, ArrowUp, ArrowDown, ExternalLink, Wrench } from "lucide-react";
import { getPortalUrl } from "@/lib/netzbetreiber";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Upload, Paperclip, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotesCell } from "@/components/NotesCell";

// Textarea mit Debounced-Autosave — synct Server-Updates nur wenn User gerade nicht editiert
function AnmeldungNotesEditor({ value, onSave, testid }) {
  const [local, setLocal] = useState(value || "");
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const latestRef = useRef(local);
  latestRef.current = local;
  useEffect(() => { if (!dirtyRef.current) setLocal(value || ""); }, [value]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const scheduleSave = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (next !== (value || "")) { dirtyRef.current = false; onSave(next); }
    }, 800);
  };
  const flushSave = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (dirtyRef.current && latestRef.current !== (value || "")) {
      dirtyRef.current = false; onSave(latestRef.current);
    } else { dirtyRef.current = false; }
  };
  return (
    <Textarea
      rows={3}
      placeholder="Notizen zur Anmeldung — Admin & Sachbearbeiter sehen diese Notizen beide."
      value={local}
      onChange={(e) => { dirtyRef.current = true; setLocal(e.target.value); scheduleSave(e.target.value); }}
      onBlur={flushSave}
      className="mt-1.5"
      data-testid={testid}
    />
  );
}

const ALL = "__all__";
const UNASSIGNED = "__unassigned__";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function Anmeldungen() {
  const { user } = useAuth();
  const isStaff = ["admin", "innendienst"].includes(user?.role);
  const isSachbearbeiter = user?.role === "sachbearbeiter";
  const [items, setItems] = useState([]);
  const [auftraggeber, setAuftraggeber] = useState([]);
  const [sachbearbeiter, setSachbearbeiter] = useState([]);
  const [selectedAg, setSelectedAg] = useState(ALL);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [typFilter, setTypFilter] = useState("alle");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [termAnmeldung, setTermAnmeldung] = useState(null);
  const [termDatum, setTermDatum] = useState("");
  const [termUhrzeit, setTermUhrzeit] = useState("");
  const [termMonteur, setTermMonteur] = useState("");
  const [detailItem, setDetailItem] = useState(null);
  const [detailProject, setDetailProject] = useState(null);
  const [filesDropOver, setFilesDropOver] = useState(false);
  const [filesUploading, setFilesUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Load linked project (with photos) when opening an Anmeldung detail
  useEffect(() => {
    setDetailProject(null);
    if (detailItem?.projekt_id) {
      api.get(`/projects/${detailItem.projekt_id}`).then((res) => setDetailProject(res.data)).catch(() => {});
    }
  }, [detailItem?.projekt_id]);

  const load = async () => {
    const [a, ag, sb] = await Promise.all([
      api.get("/anmeldungen").then((x) => x.data),
      api.get("/auftraggeber").then((x) => x.data).catch(() => []),
      api.get("/sachbearbeiter").then((x) => x.data).catch(() => []),
    ]);
    setItems(a);
    setAuftraggeber(ag);
    setSachbearbeiter(sb);
    setSelectedIds(new Set());
  };
  useEffect(() => { load(); }, []);

  // Deep-link: /anmeldungen?deeplink=<id> opens that Anmeldung's detail
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get("deeplink");
    if (!target || !items.length) return;
    const hit = items.find((x) => x.id === target);
    if (hit) {
      setDetailItem(hit);
      // Clean the query param so the modal doesn't reopen after closing
      const next = new URLSearchParams(searchParams);
      next.delete("deeplink");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const unassignedCount = useMemo(() => items.filter((i) => !i.auftraggeber_id).length, [items]);

  const filtered = useMemo(() => {
    const list = items.filter((i) => {
      if (selectedAg === UNASSIGNED && i.auftraggeber_id) return false;
      if (selectedAg !== ALL && selectedAg !== UNASSIGNED && i.auftraggeber_id !== selectedAg) return false;
      if (statusFilter !== "alle" && i.status !== statusFilter) return false;
      if (typFilter !== "alle" && !(i.anlagen_typen || []).includes(typFilter)) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          i.kunde_name.toLowerCase().includes(s) ||
          (i.kunde_adresse || "").toLowerCase().includes(s) ||
          (i.kunde_ort || "").toLowerCase().includes(s) ||
          (i.zaehlernummer || "").toLowerCase().includes(s) ||
          (i.netzbetreiber || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (o) => {
      const v = o?.[sortCol];
      if (sortCol === "anlagen_typen") return (v || []).join(",");
      return v == null ? "" : v;
    };
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "de", { sensitivity: "base" }) * dir;
    });
  }, [items, q, statusFilter, typFilter, selectedAg, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const SortIcon = ({ col }) => sortCol !== col ? null : (
    sortDir === "asc" ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filtered.forEach((i) => next.delete(i.id));
    } else {
      filtered.forEach((i) => next.add(i.id));
    }
    setSelectedIds(next);
  };
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const bulkStatus = async (newStatus) => {
    try {
      await api.post("/anmeldungen/bulk/status", { ids: Array.from(selectedIds), status: newStatus });
      toast.success(`${selectedIds.size} Anmeldung(en) → ${STATUS_LABELS[newStatus]}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkDelete = async () => {
    try {
      await api.post("/anmeldungen/bulk/delete", { ids: Array.from(selectedIds), status: "angemeldet" });
      toast.success(`${selectedIds.size} Anmeldung(en) gelöscht`);
      setBulkDeleteConfirm(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/anmeldungen/${deleteId}`);
      toast.success("Gelöscht");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadAnmeldungFiles = async (fileList) => {
    if (!detailItem) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setFilesUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/anmeldungen/${detailItem.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload fehlgeschlagen: ${f.name}`);
      }
    }
    try {
      const { data: refreshed } = await api.get(`/anmeldungen/${detailItem.id}`);
      setDetailItem(refreshed);
      setItems((it) => it.map((x) => (x.id === detailItem.id ? refreshed : x)));
    } catch {}
    setFilesUploading(false);
    if (ok) toast.success(`${ok} Datei${ok === 1 ? "" : "en"} hochgeladen`);
  };

  const deleteAnmeldungFile = async (fid) => {
    if (!detailItem) return;
    if (!window.confirm("Datei wirklich löschen?")) return;
    try {
      await api.delete(`/anmeldungen/${detailItem.id}/files/${fid}`);
      const { data: refreshed } = await api.get(`/anmeldungen/${detailItem.id}`);
      setDetailItem(refreshed);
      setItems((it) => it.map((x) => (x.id === detailItem.id ? refreshed : x)));
      toast.success("Datei gelöscht");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  const createTermin = async () => {
    try {
      await api.post("/zaehlertermine", {
        anmeldung_id: termAnmeldung.id,
        termin_datum: termDatum,
        uhrzeit: termUhrzeit,
        monteur: termMonteur,
      });
      // when a Termin is created, set status to termin_vereinbart
      await api.patch(`/anmeldungen/${termAnmeldung.id}`, { status: "termin_vereinbart" });
      toast.success("Zählertermin erstellt & Status aktualisiert");
      setTermAnmeldung(null);
      setTermDatum(""); setTermUhrzeit(""); setTermMonteur("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const quickStatus = async (item, newStatus) => {
    try {
      await api.patch(`/anmeldungen/${item.id}`, { status: newStatus });
      toast.success(`Status → ${STATUS_LABELS[newStatus]}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setUnterlagen = async (item, value) => {
    try {
      const { data } = await api.patch(`/anmeldungen/${item.id}`, { unterlagen_status: value });
      toast.success(`Unterlagen → ${UNTERLAGEN_LABELS[value]}`);
      setItems((it) => it.map((x) => (x.id === item.id ? { ...x, unterlagen_status: data.unterlagen_status } : x)));
      if (detailItem?.id === item.id) setDetailItem((d) => ({ ...d, unterlagen_status: data.unterlagen_status }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveNotiz = async (item, notizen) => {
    try {
      const { data } = await api.patch(`/anmeldungen/${item.id}`, { notizen });
      setItems((it) => it.map((x) => (x.id === item.id ? { ...x, notizen: data.notizen } : x)));
      if (detailItem?.id === item.id) setDetailItem((d) => ({ ...d, notizen: data.notizen }));
      toast.success("Notiz gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };

  const assignBearbeiter = async (item, newBearbeiterId) => {
    try {
      const { data } = await api.patch(`/anmeldungen/${item.id}`, { bearbeiter_id: newBearbeiterId || null });
      toast.success(newBearbeiterId
        ? `Zugewiesen an ${bearbeiterName(newBearbeiterId) || "Sachbearbeiter"}`
        : "Zuweisung entfernt");
      // Lokal patchen statt kompletten Reload
      setItems((it) => it.map((x) => (x.id === item.id ? { ...x, bearbeiter_id: data.bearbeiter_id } : x)));
      if (detailItem?.id === item.id) setDetailItem((d) => ({ ...d, bearbeiter_id: data.bearbeiter_id }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const bearbeiterName = (id) => {
    const u = sachbearbeiter.find((s) => s.id === id);
    return u?.name || u?.email;
  };

  const openNew = () => {
    const seed = selectedAg !== ALL && selectedAg !== UNASSIGNED ? { auftraggeber_id: selectedAg } : null;
    setEditing(seed);
    setOpen(true);
  };

  const agName = (id) => auftraggeber.find((a) => a.id === id)?.name;

  const exportPdf = async () => {
    try {
      let url, filename;
      if (selectedAg !== ALL && selectedAg !== UNASSIGNED) {
        const ag = auftraggeber.find((a) => a.id === selectedAg);
        const res = await api.get(`/auftraggeber/${selectedAg}/export`, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        filename = `Auftraggeber_${(ag?.name || "export").replace(/[^a-z0-9-_]/gi, "_")}.pdf`;
      } else {
        const res = await api.get(`/anmeldungen/export/all`, { responseType: "blob" });
        url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        filename = "Anmeldungen_gesamt.pdf";
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("PDF-Export fehlgeschlagen"); }
  };

  return (
    <div className="animate-fade-up flex flex-col lg:flex-row" data-testid="anmeldungen-page">
      <aside className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-border p-4 space-y-1 lg:min-h-[calc(100vh)] overflow-x-auto">
        <div className="overline px-2 pt-2 pb-3 flex items-center gap-2">
          <Building2 className="h-3 w-3" /> Auftraggeber
        </div>
        <button onClick={() => setSelectedAg(ALL)}
          className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === ALL ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
          data-testid="ag-filter-all">
          <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Alle</span>
          <span className="text-xs opacity-70">{items.length}</span>
        </button>
        {auftraggeber.map((a) => (
          <button key={a.id} onClick={() => setSelectedAg(a.id)}
            className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === a.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            data-testid={`ag-filter-${a.id}`}>
            <span className="truncate flex-1 text-left">{a.name}</span>
            <span className="text-xs opacity-70 ml-2">{a.anmeldungen_count ?? 0}</span>
          </button>
        ))}
        <button
          onClick={() => setSelectedAg(UNASSIGNED)}
          className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${selectedAg === UNASSIGNED ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
          data-testid="ag-filter-unassigned">
          <span className="italic opacity-80">Ohne Zuordnung</span>
          <span className="text-xs opacity-70">{unassignedCount}</span>
        </button>
        {auftraggeber.length === 0 && (
          <div className="text-xs text-muted-foreground px-3 pt-3">
            Noch keine Auftraggeber. Lege welche unter „Auftraggeber" an.
          </div>
        )}
      </aside>

      <div className="flex-1 min-w-0 p-4 lg:p-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline">Netzanmeldungen</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">
              {selectedAg === ALL ? "Alle Anmeldungen"
                : selectedAg === UNASSIGNED ? "Ohne Auftraggeber"
                : agName(selectedAg) || "Anmeldungen"}
            </h1>
            <p className="text-muted-foreground mt-1">Verwalte alle Netzanmeldungen &amp; Fertigmeldungen.</p>
          </div>
          <div className="flex gap-2">
            {isStaff && (
              <>
                <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-button">
                  <FileDown className="h-4 w-4 mr-2" /> PDF-Export
                </Button>
                <Button onClick={openNew} data-testid="new-anmeldung-button">
                  <Plus className="h-4 w-4 mr-2" /> Neue Anmeldung
                </Button>
              </>
            )}
          </div>
        </div>

        {isStaff && selectedIds.size > 0 && (
          <div className="flex items-center gap-3 rounded-md border border-primary bg-primary/5 px-4 py-3" data-testid="bulk-bar">
            <span className="text-sm font-medium">{selectedIds.size} ausgewählt</span>
            <div className="h-4 w-px bg-border" />
            <Select onValueChange={bulkStatus}>
              <SelectTrigger className="w-[220px] h-9" data-testid="bulk-status-select">
                <SelectValue placeholder="Status ändern …" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`bulk-status-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="destructive" size="sm" onClick={() => setBulkDeleteConfirm(true)} data-testid="bulk-delete">
              <Trash2 className="h-4 w-4 mr-1.5" /> Löschen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">
              <X className="h-4 w-4 mr-1.5" /> Auswahl aufheben
            </Button>
          </div>
        )}

        <Card className="border-border">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Suche nach Kunde, Adresse, Zähler …" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[220px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typFilter} onValueChange={setTypFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-typ"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Anlagentypen</SelectItem>
                {Object.entries(ANLAGEN_TYPEN).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="border-border overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[900px] lg:min-w-full">
              <TableHeader>
                <TableRow>
                  {isStaff && (
                    <TableHead className="w-[40px]">
                      <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} data-testid="select-all" />
                    </TableHead>
                  )}
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("kunde_name")} data-testid="sort-kunde">
                    Kunde<SortIcon col="kunde_name" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("auftraggeber_id")} data-testid="sort-auftraggeber">
                    Auftraggeber<SortIcon col="auftraggeber_id" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("anlagen_typen")} data-testid="sort-typen">
                    Anlagentypen<SortIcon col="anlagen_typen" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("netzbetreiber")} data-testid="sort-netzbetreiber">
                    Netzbetreiber<SortIcon col="netzbetreiber" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("anmeldedatum")} data-testid="sort-datum">
                    Datum<SortIcon col="anmeldedatum" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("status")} data-testid="sort-status">
                    Status<SortIcon col="status" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("bearbeiter_id")} data-testid="sort-bearbeiter">
                    Bearbeiter<SortIcon col="bearbeiter_id" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none hover:text-foreground" onClick={() => toggleSort("unterlagen_status")} data-testid="sort-unterlagen">
                    Unterlagen<SortIcon col="unterlagen_status" />
                  </TableHead>
                  <TableHead data-testid="col-notizen">Notizen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isStaff ? 11 : 9} className="text-center text-sm text-muted-foreground py-10" data-testid="anmeldungen-empty">
                      Keine Anmeldungen gefunden.
                    </TableCell>
                  </TableRow>
                ) : filtered.map((it) => (
                  <TableRow key={it.id} data-testid={`row-${it.id}`} data-state={selectedIds.has(it.id) ? "selected" : undefined}>
                    {isStaff && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(it.id)}
                          onCheckedChange={() => toggleSelect(it.id)}
                          data-testid={`select-${it.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <button type="button" onClick={() => setDetailItem(it)}
                        className="font-medium text-left hover:text-primary hover:underline underline-offset-2 transition-colors"
                        data-testid={`kunde-link-${it.id}`}>
                        {it.kunde_name}
                      </button>
                      <div className="text-xs text-muted-foreground">{it.kunde_adresse}</div>
                      {it.projekt_id && (
                        <Link to={`/projekte?open=${it.projekt_id}`} className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary hover:underline" data-testid={`link-projekt-${it.id}`}>
                          <Wrench className="h-3 w-3" /> Verknüpftes Projekt
                        </Link>
                      )}
                      {it.zaehlerabmeldung && (it.zaehlerabmeldung_nummern?.length > 0 || it.zaehlerabmeldung_nummer) && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1">
                          <Zap className="h-3 w-3" /> Z.abmeldung ·{" "}
                          {(it.zaehlerabmeldung_nummern?.length ? it.zaehlerabmeldung_nummern : [it.zaehlerabmeldung_nummer]).filter(Boolean).join(", ")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{agName(it.auftraggeber_id) || <span className="text-muted-foreground italic">—</span>}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(it.anlagen_typen || []).map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">{ANLAGEN_TYPEN[t]}</Badge>
                        ))}
                      </div>
                      <div className="mt-1">
                        <Badge variant="secondary"
                          className={`text-[10px] ${it.anlagen_art === "erweiterung" ? "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300" : ""}`}>
                          {ANLAGEN_ART[it.anlagen_art || "neue_anlage"]}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {it.netzbetreiber ? (
                        <a
                          href={getPortalUrl(it.netzbetreiber)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                          data-testid={`portal-link-${it.id}`}
                          title="Installateurportal öffnen"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {it.netzbetreiber}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">{it.anmeldedatum}</TableCell>
                    <TableCell>
                      <Select value={it.status} onValueChange={(v) => quickStatus(it, v)}>
                        <SelectTrigger className={`w-[190px] h-8 text-xs status-${it.status}`} data-testid={`status-select-${it.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={it.bearbeiter_id || "none"}
                        onValueChange={(v) => assignBearbeiter(it, v === "none" ? null : v)}
                      >
                        <SelectTrigger
                          className={`w-[170px] h-8 text-xs ${it.bearbeiter_id ? "border-primary/50 text-foreground" : "text-muted-foreground"}`}
                          data-testid={`bearbeiter-select-${it.id}`}
                        >
                          <SelectValue placeholder="— Niemand —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" data-testid={`bearbeiter-none-${it.id}`}>— Niemand —</SelectItem>
                          {sachbearbeiter.map((sb) => (
                            <SelectItem key={sb.id} value={sb.id} data-testid={`bearbeiter-${sb.id}-${it.id}`}>
                              {sb.name || sb.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={it.unterlagen_status || "offen"} onValueChange={(v) => setUnterlagen(it, v)}>
                        <SelectTrigger
                          className={`w-[150px] h-8 text-xs ${UNTERLAGEN_CLS[it.unterlagen_status || "offen"]}`}
                          data-testid={`unterlagen-select-${it.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(UNTERLAGEN_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k} data-testid={`unterlagen-${k}-${it.id}`}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="align-top">
                      <NotesCell text={it.notizen} canEdit={isStaff || isSachbearbeiter}
                        onSave={(v) => saveNotiz(it, v)} testid={`anmeldung-notizen-${it.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isStaff && (
                          <Button variant="ghost" size="sm" onClick={() => setTermAnmeldung(it)} data-testid={`create-termin-${it.id}`} title="Zählertermin">
                            <CalendarPlus className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(it); setOpen(true); }} data-testid={`edit-${it.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {isStaff && (
                          <Button variant="ghost" size="sm" onClick={() => setDeleteId(it.id)} data-testid={`delete-${it.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <AnmeldungForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} auftraggeber={auftraggeber} />

        <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
          <AlertDialogContent data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Anmeldung wirklich löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Zugehörige Zählertermine werden ebenfalls entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel">Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={remove} data-testid="delete-confirm">Löschen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
          <AlertDialogContent data-testid="bulk-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{selectedIds.size} Anmeldungen löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Alle ausgewählten Anmeldungen und deren Zählertermine werden entfernt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={bulkDelete} data-testid="bulk-delete-confirm">Löschen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!termAnmeldung} onOpenChange={(v) => !v && setTermAnmeldung(null)}>
          <DialogContent data-testid="termin-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Zählertermin anlegen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Für: <span className="text-foreground font-medium">{termAnmeldung?.kunde_name}</span>
              </div>
              <div className="space-y-1.5">
                <Label>Datum</Label>
                <Input type="date" value={termDatum} onChange={(e) => setTermDatum(e.target.value)} data-testid="termin-date" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Uhrzeit</Label>
                  <Input type="time" value={termUhrzeit} onChange={(e) => setTermUhrzeit(e.target.value)} data-testid="termin-time" />
                </div>
                <div className="space-y-1.5">
                  <Label>Monteur</Label>
                  <Input value={termMonteur} onChange={(e) => setTermMonteur(e.target.value)} data-testid="termin-monteur" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTermAnmeldung(null)} data-testid="termin-cancel">Abbrechen</Button>
              <Button onClick={createTermin} disabled={!termDatum} data-testid="termin-submit">Termin erstellen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!detailItem} onOpenChange={(v) => !v && setDetailItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight text-2xl">{detailItem?.kunde_name}</DialogTitle>
            </DialogHeader>
            {detailItem && (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">{detailItem.kunde_adresse}</div>

                <div className="flex flex-wrap gap-2">
                  <Badge className={`status-${detailItem.status}`}>{STATUS_LABELS[detailItem.status]}</Badge>
                  <Badge variant="secondary">{ANLAGEN_ART[detailItem.anlagen_art || "neue_anlage"]}</Badge>
                  {(detailItem.anlagen_typen || []).map((t) => (
                    <Badge key={t} variant="outline">{ANLAGEN_TYPEN[t]}</Badge>
                  ))}
                  <Badge className={UNTERLAGEN_CLS[detailItem.unterlagen_status || "offen"]} data-testid="detail-unterlagen-badge">
                    📄 Unterlagen: {UNTERLAGEN_LABELS[detailItem.unterlagen_status || "offen"]}
                  </Badge>
                  {detailItem.bearbeiter_id && (
                    <Badge className="bg-primary/10 text-primary border-primary/30" data-testid="detail-bearbeiter-badge">
                      👤 {bearbeiterName(detailItem.bearbeiter_id) || "Bearbeiter"}
                    </Badge>
                  )}
                </div>

                {/* Bearbeiter-Zuweisung (nur für Staff sichtbar — sachbearbeiter-Liste ist leer für andere) */}
                {sachbearbeiter.length > 0 && (
                  <div className="rounded-md border border-border p-3 bg-primary/5" data-testid="detail-bearbeiter-section">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Zuständiger Bearbeiter</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Der zugewiesene Sachbearbeiter sieht diese Anmeldung in seinem Portal.
                        </div>
                      </div>
                      <Select
                        value={detailItem.bearbeiter_id || "none"}
                        onValueChange={(v) => assignBearbeiter(detailItem, v === "none" ? null : v)}
                      >
                        <SelectTrigger className="w-[220px] h-9 bg-background" data-testid="detail-bearbeiter-select">
                          <SelectValue placeholder="— Niemand —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Niemand —</SelectItem>
                          {sachbearbeiter.map((sb) => (
                            <SelectItem key={sb.id} value={sb.id}>{sb.name || sb.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Dateiablage — anmeldungs-weite Dokumente */}
                <div
                  className={`rounded-md border p-4 transition-colors ${filesDropOver ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-border"}`}
                  data-testid="anmeldung-files-section"
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("Files") && !filesUploading) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                      if (!filesDropOver) setFilesDropOver(true);
                    }
                  }}
                  onDragLeave={() => setFilesDropOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setFilesDropOver(false);
                    if (!filesUploading) uploadAnmeldungFiles(e.dataTransfer.files);
                  }}
                >
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <Paperclip className="h-4 w-4 text-primary" /> Dateiablage ({detailItem.files?.length || 0})
                      {filesUploading && <span className="text-[10px] text-muted-foreground animate-pulse">wird hochgeladen …</span>}
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-primary hover:underline" data-testid="anmeldung-files-upload-label">
                      <input
                        type="file"
                        multiple
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="anmeldung-files-upload-input"
                        onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; uploadAnmeldungFiles(fs); }}
                      />
                      <Upload className="h-4 w-4" /> Datei hinzufügen
                    </label>
                  </div>
                  {(detailItem.files?.length ?? 0) === 0 ? (
                    <div className={`flex flex-col items-center justify-center gap-1 py-6 border-2 border-dashed rounded-md text-xs transition-colors ${filesDropOver ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`} data-testid="anmeldung-files-empty">
                      <Upload className="h-5 w-5" />
                      <div>{filesDropOver ? "📥 Dateien loslassen zum Hochladen" : "PDF/Foto hierhin ziehen — Formulare, Urkunden, sonstige Dokumente"}</div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {detailItem.files.map((f) => {
                        const isPdf = f.path?.endsWith(".pdf");
                        return (
                          <div key={f.id} className="relative group" data-testid={`anmeldung-file-wrap-${f.id}`}>
                            <button
                              type="button"
                              onClick={() => setPreviewFile(f)}
                              className="block w-full rounded border border-border overflow-hidden hover:border-primary transition-colors text-left"
                              data-testid={`anmeldung-file-${f.id}`}
                              title={f.name}
                            >
                              {isPdf ? (
                                <div className="aspect-square flex flex-col items-center justify-center bg-muted text-muted-foreground p-2 gap-1">
                                  <FileTextIcon className="h-8 w-8 text-red-500" />
                                  <div className="text-[10px] font-medium truncate w-full text-center px-1" title={f.name}>{f.name}</div>
                                </div>
                              ) : (
                                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${f.path}`} alt={f.name} className="aspect-square object-cover w-full" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteAnmeldungFile(f.id)}
                              className="absolute top-1 right-1 bg-background/95 border border-border rounded p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-opacity"
                              data-testid={`anmeldung-file-delete-${f.id}`}
                              title="Datei löschen"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Notizen direkt unter der Dateiablage — Admin & Sachbearbeiter sehen sich gegenseitig */}
                <div className="rounded-md border border-border p-4" data-testid="detail-notizen-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">Notizen</div>
                    <span className="text-[10px] text-muted-foreground">Autosave</span>
                  </div>
                  <AnmeldungNotesEditor
                    value={detailItem.notizen || ""}
                    onSave={async (v) => {
                      try {
                        const { data } = await api.patch(`/anmeldungen/${detailItem.id}`, { notizen: v });
                        setItems((it) => it.map((x) => (x.id === detailItem.id ? { ...x, notizen: data.notizen } : x)));
                        setDetailItem((d) => ({ ...d, notizen: data.notizen }));
                      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
                    }}
                    testid="detail-notizen"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm border-t border-border pt-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Anrede</div>
                    <div className="mt-1">{ANREDE[detailItem.kunde_anrede] || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Vorname / Nachname</div>
                    <div className="mt-1">{[detailItem.kunde_vorname, detailItem.kunde_nachname].filter(Boolean).join(" ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Auftraggeber</div>
                    <div className="mt-1">{agName(detailItem.auftraggeber_id) || <span className="text-muted-foreground italic">—</span>}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Gesamtleistung</div>
                    <div className="mt-1">{detailItem.leistung_kw} kWp</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Anmeldedatum</div>
                    <div className="mt-1">{detailItem.anmeldedatum}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Fertigmeldungsdatum</div>
                    <div className="mt-1">{detailItem.fertigmeldungdatum || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Inbetriebnahmedatum</div>
                    <div className="mt-1" data-testid="detail-inbetriebnahmedatum">{detailItem.inbetriebnahmedatum || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Netzbetreiber</div>
                    <div className="mt-1">{detailItem.netzbetreiber || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Zählernummer</div>
                    <div className="mt-1">{detailItem.zaehlernummer || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">PLZ / Ort</div>
                    <div className="mt-1">{[detailItem.kunde_plz, detailItem.kunde_ort].filter(Boolean).join(" ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Installateur</div>
                    <div className="mt-1">{detailItem.installateur || "—"}</div>
                  </div>

                  {(detailItem.modul_hersteller || detailItem.modul_typ || detailItem.wechselrichter_hersteller || detailItem.wechselrichter_typ) && (
                    <div className="col-span-2 border-t border-border pt-3 mt-1">
                      <div className="overline mb-2">Photovoltaik-Komponenten</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">Modul</div>
                          <div className="mt-1">{[detailItem.modul_hersteller, detailItem.modul_typ].filter(Boolean).join(" · ") || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">Wechselrichter</div>
                          <div className="mt-1">{[detailItem.wechselrichter_hersteller, detailItem.wechselrichter_typ].filter(Boolean).join(" · ") || "—"}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(detailItem.speicher_hersteller || detailItem.speicher_typ) && (
                    <div className="col-span-2 border-t border-border pt-3">
                      <div className="overline mb-2">Batteriespeicher</div>
                      <div>{[detailItem.speicher_hersteller, detailItem.speicher_typ].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                  )}

                  {detailItem.zaehlerabmeldung && (
                    <div className="col-span-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                      <div className="text-xs uppercase tracking-wider text-amber-800 dark:text-amber-300 flex items-center gap-1">
                        <Zap className="h-3 w-3" /> Zählerabmeldung
                      </div>
                      <div className="mt-1 font-medium">
                        {(detailItem.zaehlerabmeldung_nummern?.length ? detailItem.zaehlerabmeldung_nummern : [detailItem.zaehlerabmeldung_nummer])
                          .filter(Boolean).join(", ") || "Nummer nicht angegeben"}
                      </div>
                    </div>
                  )}

                  {detailItem.document_name && (
                    <div className="col-span-2">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Dokument</div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${detailItem.document_path}`}
                        target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-2 text-primary hover:underline"
                        data-testid="detail-document-link">
                        <FileTextIcon className="h-4 w-4" /> {detailItem.document_name}
                      </a>
                    </div>
                  )}

                  {detailProject && (() => {
                    const allDocs = (detailProject.phases || []).flatMap((ph) =>
                      (ph.photos || []).map((p) => ({ ...p, phase_key: ph.key, phase_label: ph.label }))
                    );
                    if (allDocs.length === 0) return null;
                    return (
                      <div className="col-span-2 border-t border-border pt-3" data-testid="detail-project-docs">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                            <FileTextIcon className="h-3 w-3" /> Dokumente &amp; Bilder aus Projekt ({allDocs.length})
                          </div>
                          <Link
                            to={`/projekte?open=${detailProject.id}`}
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                            data-testid="detail-open-project"
                          >
                            <Wrench className="h-3 w-3" /> Projekt öffnen
                          </Link>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {allDocs.map((doc) => {
                            const isPdf = doc.path.endsWith(".pdf");
                            return (
                              <a
                                key={doc.id}
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.path}`}
                                target="_blank"
                                rel="noreferrer"
                                className="block rounded border border-border overflow-hidden hover:border-primary group"
                                title={`${doc.name} · ${doc.phase_label}`}
                                data-testid={`detail-doc-${doc.id}`}
                              >
                                {isPdf ? (
                                  <div className="aspect-square flex flex-col items-center justify-center bg-muted p-2 gap-1">
                                    <FileTextIcon className="h-7 w-7 text-red-500" />
                                    <div className="text-[10px] font-medium truncate w-full text-center px-1">{doc.name}</div>
                                  </div>
                                ) : (
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.path}`} alt={doc.name} className="aspect-square object-cover w-full" />
                                )}
                                <div className="px-1.5 py-1 bg-background border-t border-border">
                                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground truncate">{doc.phase_label}</div>
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="col-span-2 border-t border-border pt-3 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                    <div>
                      <div className="uppercase tracking-wider">Erstellt</div>
                      <div className="mt-0.5">{fmtDateTime(detailItem.created_at)}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wider">Zuletzt aktualisiert</div>
                      <div className="mt-0.5">{fmtDateTime(detailItem.updated_at)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailItem(null)} data-testid="detail-close">Schließen</Button>
              <Button onClick={() => { setEditing(detailItem); setDetailItem(null); setOpen(true); }} data-testid="detail-edit">
                <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Datei-Vorschau (Lightbox) */}
        <Dialog open={!!previewFile} onOpenChange={(v) => !v && setPreviewFile(null)}>
          <DialogContent className="max-w-5xl" data-testid="anmeldung-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight text-lg flex items-center gap-2 pr-6 min-w-0">
                {previewFile?.path?.endsWith(".pdf") ? <FileTextIcon className="h-5 w-5 text-red-500 shrink-0" /> : <ImageIcon className="h-5 w-5 text-primary shrink-0" />}
                <span className="truncate">{previewFile?.name}</span>
              </DialogTitle>
              <DialogDescription className="sr-only">Vorschau der Datei {previewFile?.name}</DialogDescription>
            </DialogHeader>
            {previewFile && (
              <div className="flex flex-col gap-3">
                <div className="bg-muted/30 rounded-md overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh", maxHeight: "75vh" }}>
                  {previewFile.path.endsWith(".pdf") ? (
                    <iframe
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewFile.path}`}
                      className="w-full h-[75vh] border-0"
                      title={previewFile.name}
                      data-testid="anmeldung-preview-iframe"
                    />
                  ) : (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewFile.path}`}
                      alt={previewFile.name}
                      className="max-w-full max-h-[75vh] object-contain"
                      data-testid="anmeldung-preview-img"
                    />
                  )}
                </div>
                <DialogFooter>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${previewFile.path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    data-testid="anmeldung-preview-open-tab"
                  >
                    <Upload className="h-3.5 w-3.5 rotate-180" /> In neuem Tab öffnen
                  </a>
                  <Button type="button" variant="outline" onClick={() => setPreviewFile(null)} data-testid="anmeldung-preview-close">Schließen</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
